<script setup>
import { computed } from "vue";
import { useRouter } from "vue-router";
import { useUploadMonitor, UPLOAD_STATUS } from "../../composables/useUploadMonitor.js";
import Spinner from "./Spinner.vue";

const router = useRouter();
const { files, isVisible, isMinimized, hide, toggleMinimize } = useUploadMonitor();

const IN_PROGRESS = [UPLOAD_STATUS.JUST_ADDED, UPLOAD_STATUS.UPLOADING, UPLOAD_STATUS.PENDING];

const title = computed(() => {
  const inProgress = files.value.filter((f) => IN_PROGRESS.includes(f.status));
  if (inProgress.length) return `Uploading ${inProgress.length} item${inProgress.length > 1 ? "s" : ""}`;
  const done = files.value.filter((f) => f.status === UPLOAD_STATUS.DONE).length;
  return `${done} upload${done === 1 ? "" : "s"} complete`;
});

function friendlyMessage(entry) {
  if (entry.status !== UPLOAD_STATUS.ERROR) return "";
  if (`${entry.message}`.includes("EXCEEDS_SIZE")) return "Exceeds the 200MB file size limit.";
  if (`${entry.message}`.includes("UNSUPPORTED_FILE_TYPE")) return "File format is not supported.";
  return "File cannot be uploaded. Please try again.";
}

function openDocument(entry) {
  if (entry.status !== UPLOAD_STATUS.DONE) return;
  isMinimized.value = true;
  router.push({ name: "v2-document", params: { projectId: entry.projectId, docId: entry.documentId ?? entry.id } });
}
</script>

<template>
  <div
    v-if="isVisible"
    class="fixed bottom-6 right-6 z-[300] w-[360px] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl overflow-hidden"
  >
    <div class="flex items-center justify-between px-4 py-3">
      <span class="text-sm font-bold text-slate-800 dark:text-slate-100">{{ title }}</span>
      <div class="flex items-center gap-1 shrink-0">
        <button
          class="flex items-center justify-center w-6 h-6 rounded text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-600 dark:hover:text-slate-200 border-0 bg-transparent cursor-pointer transition-colors"
          @click="toggleMinimize"
        >
          <svg v-if="isMinimized" width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path d="M18 15l-6-6-6 6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <button
          class="flex items-center justify-center w-6 h-6 rounded text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-600 dark:hover:text-slate-200 border-0 bg-transparent cursor-pointer transition-colors"
          @click="hide"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" />
          </svg>
        </button>
      </div>
    </div>

    <div v-if="!isMinimized" class="border-t border-slate-100 dark:border-slate-700 max-h-[320px] overflow-y-auto py-1">
      <p v-if="!files.length" class="text-sm text-slate-400 dark:text-slate-500 text-center py-8 m-0">Nothing to upload</p>
      <div v-for="entry in files" :key="entry.id" class="flex items-center gap-3 px-4 py-2.5">
        <div class="flex-1 min-w-0">
          <button
            :class="entry.status === 'done' ? 'hover:text-brand hover:underline cursor-pointer' : 'cursor-default'"
            class="block w-full text-left text-sm font-medium text-slate-800 dark:text-slate-100 truncate border-0 bg-transparent p-0"
            @click="openDocument(entry)"
          >
            {{ entry.name }}
          </button>
          <p v-if="friendlyMessage(entry)" class="text-xs text-red-500 dark:text-red-400 m-0 mt-0.5">{{ friendlyMessage(entry) }}</p>
        </div>

        <Spinner v-if="IN_PROGRESS.includes(entry.status)" size="16px" />
        <svg v-else-if="entry.status === 'done'" width="18" height="18" viewBox="0 0 24 24" fill="none" class="text-green-500 shrink-0">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8" />
          <path d="M8 12.5l2.5 2.5L16 9.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" class="text-red-500 shrink-0" :title="friendlyMessage(entry)">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8" />
          <path d="M12 8v5M12 16h.01" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
        </svg>
      </div>
    </div>
  </div>
</template>
